import type { Page } from './utils';
import { fmtDec, fmtDuration, fmtInt, fmtTime, groupByTimezone, hasCause } from './utils';
import { causeMeta } from './errorCatalog';
import s from './BroadcastDetailsModal.module.css';

/**
 * O rótulo de uma execução. `ok` sozinho significa apenas "não falhou", então
 * pendente e em andamento eram exibidos como "Enviado" — num grupo agendado,
 * as 293 linhas diziam enviado antes de qualquer disparo.
 */
const enviou = (p: Page) => p.status === 'sent' || p.status === 'completed';

function rotuloDe(p: Page): string {
  if (p.status === 'failed') return 'Falhou';
  if (p.status === 'cancelled') return 'Cancelado';
  if (p.status === 'pending') return 'Agendado';
  if (p.status === 'processing' || p.status === 'sending') return 'Enviando';
  if (p.status === 'translating') return 'Traduzindo';
  return p.iniciou ? 'Enviado' : 'Agendado';
}

function pillDe(p: Page, s: Record<string, string>): string {
  if (p.status === 'failed') return s.pillFail;
  if (p.status === 'processing' || p.status === 'sending' || p.status === 'translating') return s.pillRun;
  if (p.status === 'pending' || p.status === 'cancelled') return s.pillWait;
  return p.iniciou ? s.pillOk : s.pillWait;
}


interface Props { pages: Page[]; cause: string | null; appName: (id: number) => string }

export function ExecutionsTab({ pages, cause, appName }: Props) {
  const groups = groupByTimezone(pages);
  if (!groups.length) return <div className={s.empty}>Nenhuma página encontrada</div>;
  return (
    <div className={s.scrollPad}>
      {groups.map((g) => {
        const late = g.pages.filter((p) => p.delay > 1).length;
        return (
          <div key={g.tz} className={s.group}>
            <div className={s.groupHead}>
              <span>{g.tz} — {g.pages.length} páginas</span>
              {/* Conta o que SAIU, não o que "não falhou": num grupo agendado isto
                  dizia "30 enviadas" antes do primeiro disparo. */}
              <span className={s.muted}>
                {g.pages.filter(enviou).length} enviadas
                {g.pages.filter((p) => !enviou(p) && p.status !== 'failed').length > 0
                  && ` · ${g.pages.filter((p) => !enviou(p) && p.status !== 'failed').length} a enviar`}
                {late ? ` · ${late} com atraso >1 min` : ''}
              </span>
            </div>
            <div className={`${s.execGrid} ${s.tHead}`}>
              <span>Página</span><span>Status</span><span>Agendado</span><span>Disparou</span><span>Concluiu</span><span>Duração</span><span>Atraso</span><span>Env / Ent</span><span className={s.right}>Falhas</span>
            </div>
            {g.pages.map((p) => (
              <div key={p.id} className={`${s.execGrid} ${s.tRow}`} style={{ opacity: cause && !hasCause(p, cause) ? 0.3 : 1 }}>
                <div className={s.nameCell}><b>{p.name}</b><small>{appName(p.app)}</small></div>
                {/* Quatro estados, não dois: `ok` só diz "não falhou", e com isso um
                    broadcast AGENDADO aparecia como "Enviado". */}
                <span><span className={pillDe(p, s)}>{rotuloDe(p)}</span></span>
                <span className={s.muted}>{fmtTime(p.sch)}</span>
                {/* Sem started_at/completed_at não há o que mostrar: antes caía no
                    horário agendado e a linha dizia "disparou e concluiu em 0s". */}
                <span>{p.iniciou ? fmtTime(p.s, true) : <span className={s.muted}>—</span>}</span>
                <span>{p.concluiu ? fmtTime(p.e, true) : <span className={s.muted}>—</span>}</span>
                <span>{p.iniciou && p.concluiu ? fmtDuration(p.e - p.s) : <span className={s.muted}>—</span>}</span>
                <span style={{ color: p.iniciou && p.delay > 1 ? '#c2410c' : '#6b7280', fontWeight: 600 }}>
                    {p.iniciou ? `${fmtDec(p.delay)} min` : '—'}
                </span>
                <span className={s.envEnt}>{fmtInt(p.sent)} <i>/</i> <em>{fmtInt(p.deliv)}</em></span>
                <span className={s.failCell} title={p.errs.map((e) => `${fmtInt(e.n)} — ${causeMeta(e.key, e.message).description}`).join('\n')}>
                  {p.errs.map((e) => <i key={e.key} className={s.sqSm} style={{ background: causeMeta(e.key).color }} />)}
                  <b style={{ color: p.fail ? '#dc2626' : '#9ca3af' }}>
                    {p.fail ? fmtInt(p.fail) : p.errs.some((e) => e.key === 'sem_lead_elegivel') ? 'sem leads' : '–'}
                  </b>
                </span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
